/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link, useParams } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatUTCDate, formatLastActive } from "../../utils/utils";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import "../active-users/active-users.css";
import "./UserBioVitals.css";

const LIMIT = 20;
const EXPORT_LIMIT = 100000;

/**
 * Shared per-type Bio Vitals detail-list page (029, inner pages) reconstructed on the
 * shared au- design system — used by the BP / Sugar / Oxygen / Salt / Water detail
 * routes reached from the aggregate table's per-type Count drill-down. Config-driven
 * so all five pages stay 1:1 consistent. Read-only; reuses the existing
 * `/admin/api/<type>-details` endpoint (page/limit/from_date/to_date/user_id).
 *
 * config: { title, routePath, endpoint, valueLabel, valueKey, dateLabel, dateKey,
 *           dateFmt:'utc'|'last', totalLabel, breadcrumbType, exportName }
 */
const BioVitalDetailList = ({ config }) => {
  const {
    title, routePath, endpoint, valueLabel, valueKey, dateLabel, dateKey,
    dateFmt, totalLabel, breadcrumbType, exportName,
  } = config;
  const { id } = useParams();
  const navigate = useNavigate();
  const fmtDate = dateFmt === 'last' ? formatLastActive : formatUTCDate;

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (cfg) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      cfg.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return cfg;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // No default date window — the detail page opens all-time; the user can
    // narrow with the date range bar.
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => { if (token) loadData(page, fromDate, toDate); }, [token]);

  const userId = id ? atob(id) : '';
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const qs = (p, f, t, lim) => `page=${p}&limit=${lim}&from_date=${f}&to_date=${t}&user_id=${userId}`;

  const loadData = async (p, f, t) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}${endpoint}?${qs(p, f, t, LIMIT)}`, auth());
      setData(res.data.data || []);
      setTotalItem(res.data.totalItem || 0);
    } catch (err) {
      setError(err?.response?.status === 401 ? 'auth' : 'load');
    } finally { setLoading(false); }
  };

  const handlePageChange = (p) => { setPage(p); loadData(p, fromDate, toDate); };
  const handleFromChange = (d) => { setPreset('custom'); setDisplayFromDate(d); setFromDate(d ? fmt(d) : ''); };
  const handleToChange = (d) => { setPreset('custom'); setDisplayToDate(d); setToDate(d ? fmt(d) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr || fs); setToDate(toStr || ts);
    setDisplayFromDate(new Date(fromStr || fs)); setDisplayToDate(new Date(toStr || ts));
    setPage(1); loadData(1, fromStr || fs, toStr || ts);
  };
  const handleSearch = () => { setPage(1); loadData(1, fromDate, toDate); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setPreset(null); setPage(1); loadData(1, '', '');
  };

  const EXPORT_COLUMNS = [
    { key: 'row_no', label: 'No' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'value', label: valueLabel },
    { key: 'date', label: dateLabel },
  ];
  const fetchExport = async () => {
    const res = await axiosJWT.get(`${baseUrl}${endpoint}?${qs(1, fromDate, toDate, EXPORT_LIMIT)}`, auth());
    return (res.data.data || []).map((r, i) => ({
      row_no: i + 1, name: r.name, email: r.email,
      value: r[valueKey], date: r[dateKey] ? fmtDate(r[dateKey]) : '',
    }));
  };

  const hasRange = !!(fromDate || toDate);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Health › Bio vitals › <b>{breadcrumbType}</b></span>
            <span className="au-route">{routePath}</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/user-bio-vitals">← Back</Link></span>
          </div>
          <h1 className="au-title">{title}</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load these readings. Please try again." onRetry={() => loadData(page, fromDate, toDate)} />
          ) : (
            <>
              <div className="au-cards">
                <div className="au-card">
                  <span className="au-card__label">{totalLabel}</span>
                  <span className="au-card__value tnum">{Number(totalItem || 0).toLocaleString()}</span>
                </div>
              </div>

              <DateRangeBar
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasRange}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="bvd-range-note">
                The date range filters readings by their <b>activity date (UTC)</b> — the {dateLabel.toLowerCase()} shown per row.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all readings (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : data.length === 0 ? (
                <EmptyState title="No readings in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No readings match the current filters.' : 'There are no readings to show for this view yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">{title}</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename={exportName} />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table bv-table" data-testid="bvd-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>{valueLabel}</th><th>{dateLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((item, index) => (
                          <tr key={item.id}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{item.name}</td>
                            <td data-label="Email" className="au-ellipsis">{item.email}</td>
                            <td data-label={valueLabel} className="tnum">{item[valueKey]}</td>
                            <td data-label={dateLabel}>{item[dateKey] ? fmtDate(item[dateKey]) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BioVitalDetailList;
